import { useTranslation } from "react-i18next";
import type { JSX } from "react";
import { useMemo } from "react";

import {
  Pagination as PaginationBase,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./paginationBase";
import type { IPaginationProps } from "./types";

export function Pagination({
  totalPages,
  currentPage,
  onPageChange,
  // perPage,
  // onPerPageChange,
  nextLabel = "nextPage",
  beforeLabel = "beforePage",
}: IPaginationProps) {
  const { t } = useTranslation("common");

  const startPage = useMemo(() => Math.max(currentPage - 1, 1), [currentPage]);
  const endPage = useMemo(
    () => Math.min(currentPage + 1, totalPages),
    [currentPage, totalPages],
  );

  const renderPageNumbers = () => {
    const pages: JSX.Element[] = [];

    if (startPage > 1) {
      pages.push(
        <PaginationItem
          key={"start"}
          onClick={() => {
            onPageChange(1);
          }}
        >
          <PaginationLink>{"1"}</PaginationLink>
        </PaginationItem>,
      );
      if (startPage > 2) {
        pages.push(<PaginationItem key={"ellipsis1"}>{"…"}</PaginationItem>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem
          key={i.toString()}
          isActive={currentPage === i}
          onClick={() => {
            onPageChange(i);
          }}
        >
          <PaginationLink isActive={currentPage === i}>{i}</PaginationLink>
        </PaginationItem>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<PaginationItem key={"ellipsis2"}>{"…"}</PaginationItem>);
      }
      pages.push(
        <PaginationItem
          key={"end"}
          onClick={() => {
            onPageChange(totalPages);
          }}
        >
          <PaginationLink>{totalPages}</PaginationLink>
        </PaginationItem>,
      );
    }

    return pages;
  };

  return (
    <PaginationBase aria-label={"Page navigation"}>
      <PaginationContent>
        <PaginationPrevious
          onClick={() => {
            onPageChange(currentPage - 1 > 0 ? currentPage - 1 : 1);
          }}
          disabled={currentPage === 1}
        >
          {t(beforeLabel)}
        </PaginationPrevious>
        {renderPageNumbers()}
        <PaginationNext
          onClick={() => {
            onPageChange(
              currentPage + 1 < totalPages ? currentPage + 1 : totalPages,
            );
          }}
          disabled={currentPage === totalPages}
        >
          {t(nextLabel)}
        </PaginationNext>
      </PaginationContent>
    </PaginationBase>
  );
}
